import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { authErrorKey } from "@/lib/auth-error";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Şifre Sıfırla — JUËN" },
      { name: "description", content: "Yeni JUËN şifrenizi belirleyin." },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN" || (event === "INITIAL_SESSION" && session)) {
        setReady(true);
      }
    });

    const init = async () => {
      try {
        const url = new URL(window.location.href);
        const hash = window.location.hash.startsWith("#")
          ? window.location.hash.slice(1)
          : "";
        const hashParams = new URLSearchParams(hash);

        const errorDesc =
          url.searchParams.get("error_description") ??
          hashParams.get("error_description");
        if (errorDesc) {
          toast.error(t(authErrorKey(decodeURIComponent(errorDesc.replace(/\+/g, " ")))));
          return;
        }

        // Implicit flow: tokens are in the URL hash
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");
        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            toast.error(t(authErrorKey(error.message)));
            return;
          }
          window.history.replaceState({}, "", url.pathname + url.search);
          setReady(true);
          return;
        }

        // Token hash flow: ?token_hash=...&type=recovery
        const tokenHash = url.searchParams.get("token_hash");
        if (tokenHash) {
          const { error } = await supabase.auth.verifyOtp({
            type: "recovery",
            token_hash: tokenHash,
          });
          if (error) {
            toast.error(t(authErrorKey(error.message)));
            return;
          }
          window.history.replaceState({}, "", url.pathname);
          setReady(true);
          return;
        }

        // PKCE flow: ?code=...
        const code = url.searchParams.get("code");
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            toast.error(t(authErrorKey(error.message)));
            return;
          }
          url.searchParams.delete("code");
          window.history.replaceState({}, "", url.pathname + url.search);
          setReady(true);
          return;
        }

        // Fallback: session may already be set
        const { data } = await supabase.auth.getSession();
        if (data.session) setReady(true);
      } catch (err) {
        console.error("reset-password init error", err);
      }
    };
    void init();

    return () => sub.subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t("reset.tooShort"));
      return;
    }
    if (password !== confirm) {
      toast.error(t("reset.mismatch"));
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      toast.error(t(authErrorKey(error.message)));
      return;
    }
    toast.success(t("reset.done"));
    void navigate({ to: "/dashboard" });
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight">{t("reset.title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("reset.subtitle")}
          </p>
        </div>

        {!ready ? (
          <div className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground shadow-card md:p-8">
            {t("reset.verifying")}
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-5 rounded-2xl border border-border bg-card p-6 shadow-card md:p-8"
          >
            <div className="space-y-2">
              <Label htmlFor="password">{t("reset.new")}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">{t("reset.confirm")}</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <Button type="submit" disabled={loading} className="h-12 w-full text-base">
              {loading ? t("reset.updating") : t("reset.submit")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
